import type { CurrencyCode } from '@shared/hooks/useGeoLocale';
import { asCurrencyCode, useGeoBasedLocale } from '@shared/hooks/useGeoLocale';

/**
 * Geo currency narrowed to the cards-app supported subset.
 *
 * `useGeoBasedLocale` returns the full `CurrencyCodeType` (all ISO codes), but
 * cards only supports the `CurrencyCode` enum. The picker, browser defaults, and
 * localStorage all flow `CurrencyCode` values in, so the narrowing should be a
 * no-op in practice — it defends against an out-of-set value sneaking in via a
 * manual `?currency=XXX` query param.
 *
 * Also adds type safety: cards-app APIs like `useMinorUnitsToPriceString` type
 * `currency` as `CurrencyCode`, so the raw `CurrencyCodeType` from
 * `useGeoBasedLocale` isn't directly assignable — this hook returns the narrowed
 * type so callers can pass it through without their own conversion.
 *
 * Returns `undefined` when the geo currency falls outside `CurrencyCode`;
 * callers decide their own fallback.
 */
export const useCardGeoCurrency = (): CurrencyCode | undefined => {
  const { currency } = useGeoBasedLocale();
  return asCurrencyCode(currency);
};
